import '../../styles.scoped.css'
import {Typography, Button, Chip} from '@mui/material'
import {Edit} from '@mui/icons-material'
import {ENVIRONMENTS, QUALIFIERS} from '../../../AdminOrganizationDetails/components'
import {labelOf, branchNeedsEvv} from '../../helpers'
import {evvChip} from '../Branches'
import {fullName, branchNames, roleName} from '../Users'

const Section = ({title, onEdit, children}) => (
    <div className="review-section">
        <div className="card-header">
            <Typography variant="h7" color="primary">
                {title}
            </Typography>
            <Button startIcon={<Edit fontSize="small" />} onClick={onEdit}>
                Edit
            </Button>
        </div>
        {children}
    </div>
)

const Row = ({label, children}) => (
    <div className="review-row">
        <Typography className="review-label" variant="h8" color="text.primary">
            {label}
        </Typography>
        <Typography variant="body1" color="text.primary" component="div">
            {children || '—'}
        </Typography>
    </div>
)

const addressOf = address => [address?.street1, address?.street2, address?.city, address?.state, address?.zip].filter(Boolean).join(', ')

export default ({general, logoUrl, branches, evv, evvNeeded, users, roles, serviceCatalog, evvServiceIds, goTo}) => (
    <>
        <Typography variant="h6" color="text.primary">
            Review & create
        </Typography>
        <Typography className="panel-subtitle" variant="body2" color="text.secondary">
            Check everything below, then create the organization.
        </Typography>

        <Section title="General Information" onEdit={() => goTo(0)}>
            <Row label="Logo">{logoUrl && <img src={logoUrl} height={48} alt="Healthcare logo" />}</Row>
            <Row label="Healthcare Name">{general.name}</Row>
            <Row label="Email">{general.email}</Row>
            <Row label="Phone Number">{general.phone}</Row>
            {/* Not asked for yet: the organization is created without one. */}
            <Row label="Business Address" />
            <Row label="Organization NPI">{general.npi}</Row>
            <Row label="US Taxpayer ID (TIN)">{general.tin}</Row>
            <Row label="Federal Tax ID (EIN)">{general.federal_tax_id}</Row>
        </Section>

        <Section title={`Branches (${branches.length})`} onEdit={() => goTo(1)}>
            {branches.map(branch => (
                <div className="review-card" key={branch.key}>
                    <div className="review-card-title">
                        <Typography variant="h9" color="primary">
                            {branch.branch_name}
                            {branch.main ? ' (HQ)' : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {[branch.address.city, branch.address.state].filter(Boolean).join(', ')}
                        </Typography>
                    </div>
                    <Typography variant="body2" color="text.primary">
                        <b>Email:</b> {branch.email || '—'} &nbsp;|&nbsp; <b>Phone Number:</b> {branch.phone_number || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.primary" style={{marginTop: 6}}>
                        <b>Branch NPI:</b> {branch.provider_number} &nbsp;|&nbsp; <b>UMPI:</b> {branch.umpi || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.primary" style={{marginTop: 6}}>
                        <b>Branch Address:</b>
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                        {addressOf(branch.address)}
                    </Typography>
                    {branchNeedsEvv(branch, evvServiceIds) && (
                        <Typography variant="body2" color="text.primary" style={{marginTop: 6}}>
                            <b>EVV office:</b> {labelOf(QUALIFIERS, branch.evv.office_qualifier)} {branch.evv.office_identifier}
                        </Typography>
                    )}
                    <br />
                    <Typography variant="overline" color="text.secondary">
                        SERVICES
                    </Typography>
                    <div className="service-boxes">
                        {branch.service_ids.map(id => {
                            const service = serviceCatalog.find(item => item.id === id)
                            return (
                                <div className="service-box" key={id}>
                                    <Typography variant="h9" color="text.primary">
                                        {service?.name || id}
                                    </Typography>
                                    {evvChip(service?.requires_evv)}
                                </div>
                            )
                        })}
                    </div>
                    <br />
                    <Typography variant="overline" color="text.secondary">
                        PAYORS
                    </Typography>
                    {branch.payers.length ? (
                        branch.payers.map(payer => (
                            <div className="card-header" key={`${payer.payer_name}-${payer.service_category}`} style={{marginBottom: 8}}>
                                <div>
                                    <Typography variant="h9" color="text.primary">
                                        {payer.payer_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        ID: {payer.provider_number}
                                    </Typography>
                                </div>
                                <Chip label={`EVV code: ${payer.payer_code}`} variant="outlined" color="success" size="small" />
                            </div>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No payors
                        </Typography>
                    )}
                </div>
            ))}
        </Section>

        {evvNeeded && (
            <Section title="EVV credentials (org-wide)" onEdit={() => goTo(2)}>
                <Row label="Client ID">{evv.client_id?.trim() || 'Not set — can be added later from the EVV tab'}</Row>
                {evv.client_id?.trim() && (
                    <>
                        <Row label="Environment">{labelOf(ENVIRONMENTS, evv.base_url)}</Row>
                        <Row label="API version">{evv.api_version}</Row>
                    </>
                )}
            </Section>
        )}

        <Section title={`Users (${users.length})`} onEdit={() => goTo(3)}>
            {users.map(user => (
                <div className="review-card" key={user.email}>
                    <Typography variant="h9" color="primary">
                        {fullName(user)}
                        <Typography component="span" variant="body2" color="text.secondary">
                            {' '}
                            · {roleName(roles, user.role_id)}
                        </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                        {[user.email.trim(), user.phone?.trim()].filter(Boolean).join(' | ')}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                        Branches: {branchNames(user, branches)}
                    </Typography>
                </div>
            ))}
        </Section>
    </>
)
